import { workshopSessionSchema } from '@mio/shared';
import { useQuery } from '@tanstack/react-query';

import { ApiRequestError } from '@/shared/api/api-error';
import { apiRequest } from '@/shared/api/client';
import { workspaceQuery } from '@/shared/api/queries/workspace';
import { continueAtFor } from '@/shared/program/continue-at';
import { useStudioResume } from '@/shared/program/use-studio-resume';

import type { StudioModel } from '@/shared/program/use-studio';

/** Resolve once before navigating: a late workspace response must never open a second activity. */
export function useStudioResumeTarget(studio: StudioModel | undefined) {
  const { visit } = useStudioResume();
  const saved = visit?.programSlug === studio?.programSlug ? visit : null;
  const listed = studio?.sequence.find(
    (item) => item.id === saved?.activityId && item.kind === saved.kind,
  );
  const checkEarlier = Boolean(studio && saved?.kind === 'workshop' && !listed);
  // The session read authorizes an older saved attempt without starting compute.
  const earlier = useQuery({
    queryKey: ['studio', 'resume-workshop', saved?.activityId ?? null],
    enabled: checkEarlier,
    queryFn: ({ signal }) =>
      apiRequest(
        `/workshop/activities/${encodeURIComponent(saved?.activityId ?? '')}`,
        workshopSessionSchema,
        { signal },
      ),
    retry: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
  const gone =
    earlier.error instanceof ApiRequestError && [403, 404, 410].includes(earlier.error.status);
  const settledEarlier = !earlier.isPending && !earlier.isFetching;
  const ownedEarlier = Boolean(
    settledEarlier &&
    !earlier.error &&
    earlier.data?.entitled &&
    earlier.data.task &&
    earlier.data.attempt &&
    earlier.data.attempt.status !== 'abandoned',
  );
  const fallbackNeeded = Boolean(
    studio &&
    !listed &&
    (!checkEarlier || (settledEarlier && (gone || (!earlier.error && !ownedEarlier)))),
  );
  const workspace = useQuery({
    ...workspaceQuery,
    enabled: fallbackNeeded,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchInterval: false,
    retry: false,
  });

  if (listed)
    return {
      target: { id: listed.id, kind: listed.kind },
      pending: false,
      error: null,
      retry: () => undefined,
    };
  if (checkEarlier) {
    if (!settledEarlier)
      return { target: null, pending: true, error: null, retry: () => void earlier.refetch() };
    if (earlier.error && !gone)
      return {
        target: null,
        pending: false,
        error: earlier.error,
        retry: () => void earlier.refetch(),
      };
    if (ownedEarlier && saved)
      return {
        target: { id: saved.activityId, kind: 'workshop' as const },
        pending: false,
        error: null,
        retry: () => undefined,
      };
  }
  if (!studio || workspace.isPending || workspace.isFetching)
    return { target: null, pending: true, error: null, retry: () => void workspace.refetch() };
  if (workspace.error)
    return {
      target: null,
      pending: false,
      error: workspace.error,
      retry: () => void workspace.refetch(),
    };
  const fallback = continueAtFor(studio, workspace.data) ?? studio.sequence[0] ?? null;
  return {
    target: fallback ? { id: fallback.id, kind: fallback.kind } : null,
    pending: false,
    error: null,
    retry: () => void workspace.refetch(),
  };
}
